/**
 * Centralized time formatting utilities
 *
 * This module provides consistent time formatting across the application.
 * Use these functions instead of defining local formatTime functions.
 */

export interface FormatTimeOptions {
  /**
   * If true, omits minutes when they are :00 (e.g., "3pm" instead of "3:00pm")
   * @default true
   */
  condensed?: boolean;

  /**
   * If true, uses uppercase AM/PM, otherwise lowercase am/pm
   * @default false
   */
  uppercase?: boolean;

  /**
   * If true, adds a space before AM/PM (e.g., "3:00 PM" instead of "3:00PM")
   * @default false
   */
  spaceBeforePeriod?: boolean;
}

/**
 * Formats a 24-hour time string (HH:MM) into 12-hour format with am/pm
 *
 * @param time - Time string in "HH:MM" format (e.g., "13:30", "09:00")
 * @param options - Formatting options
 * @returns Formatted time string (e.g., "1:30pm", "9am", "1:30 PM")
 *
 * @example
 * formatTime("13:30") // "1:30pm"
 * formatTime("09:00") // "9am"
 * formatTime("09:00", { condensed: false }) // "9:00am"
 * formatTime("13:30", { uppercase: true, spaceBeforePeriod: true }) // "1:30 PM"
 */
export const formatTime = (time: string, options: FormatTimeOptions = {}): string => {
  const { condensed = true, uppercase = false, spaceBeforePeriod = false } = options;

  // Handle datetime strings by extracting just the time portion
  const timeStr = time.includes(' ') ? time.substring(11) : time;
  const [hoursStr, minutesStr] = timeStr.split(':');
  const hour = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // Determine display hour (convert 24h to 12h)
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  // Determine period
  const period = hour >= 12 ? (uppercase ? 'PM' : 'pm') : uppercase ? 'AM' : 'am';

  // Build the time string
  const space = spaceBeforePeriod ? ' ' : '';
  const showMinutes = !condensed || minutes !== 0;

  if (showMinutes) {
    return `${displayHour}:${minutesStr}${space}${period}`;
  }
  return `${displayHour}${space}${period}`;
};

/**
 * Formats time in condensed format with lowercase am/pm (most common)
 * Example: "13:30" → "1:30pm", "09:00" → "9am"
 */
export const formatTimeCondensed = (time: string): string => {
  return formatTime(time, { condensed: true, uppercase: false, spaceBeforePeriod: false });
};

/**
 * Formats time with all digits (always shows minutes) and lowercase am/pm
 * Example: "13:30" → "1:30pm", "09:00" → "9:00am"
 */
export const formatTimeFull = (time: string): string => {
  return formatTime(time, { condensed: false, uppercase: false, spaceBeforePeriod: false });
};

/**
 * Formats time in condensed format with uppercase AM/PM and space
 * Example: "13:30" → "1:30 PM", "09:00" → "9 AM"
 */
export const formatTimeCondensedUppercase = (time: string): string => {
  return formatTime(time, { condensed: true, uppercase: true, spaceBeforePeriod: true });
};

/**
 * Formats time with all digits and uppercase AM/PM with space
 * Example: "13:30" → "1:30 PM", "09:00" → "9:00 AM"
 */
export const formatTimeFullUppercase = (time: string): string => {
  return formatTime(time, { condensed: false, uppercase: true, spaceBeforePeriod: true });
};

/**
 * Formats a time range using condensed format
 * Example: "08:00", "17:00" → "8am–5pm"
 *
 * @param startTime - Start time in "HH:MM" format
 * @param endTime - End time in "HH:MM" format
 * @param options - Formatting options (applied to both times)
 * @returns Formatted time range string
 */
export const formatTimeRange = (
  startTime: string,
  endTime: string,
  options: FormatTimeOptions = {},
): string => {
  return `${formatTime(startTime, options)}–${formatTime(endTime, options)}`;
};

/**
 * Formats a time range using condensed format (convenience function)
 * Example: "08:00", "17:00" → "8am–5pm"
 */
export const formatTimeRangeCondensed = (startTime: string, endTime: string): string => {
  return formatTimeRange(startTime, endTime, {
    condensed: true,
    uppercase: false,
    spaceBeforePeriod: false,
  });
};

/**
 * Formats a duration in seconds as "M:SS" format
 * Example: 90 → "1:30", 5 → "0:05"
 *
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "5:30", "0:05")
 */
export const formatDurationFromSeconds = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) {
    return '0:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

/**
 * Formats a duration in minutes as "Xm Ys" format
 * Example: 1.5 → "1m 30s", 0.5 → "30s"
 *
 * @param minutes - Duration in minutes (can be fractional)
 * @returns Formatted duration string (e.g., "5m 30s", "30s")
 */
export const formatDurationFromMinutes = (minutes: number): string => {
  const wholeMinutes = Math.floor(minutes * 60);
  const remainingSeconds = Math.round((minutes * 60 - wholeMinutes) * 60);
  if (wholeMinutes === 0) {
    return `${remainingSeconds}s`;
  }
  return `${wholeMinutes}m ${remainingSeconds}s`;
};
